// client/src/lib/hooks/useCallProvider.ts
import { useState, useEffect } from 'react';
import { ICallProvider } from '../call-providers/ICallProvider';
import kioCallProvider from '../call-providers/KioCallProvider';
import logger from '../helpers/logger';

interface UseCallProviderResult {
	isGroupAvailable: boolean;
	isCallActive: boolean;
	callGroup: () => void;
}

// Note: provider should be a stable reference (ex. useRef) to avoid re-runs on every render.
export function useCallProvider(groupId: string | null | undefined, provider: ICallProvider = kioCallProvider): UseCallProviderResult {
	const [isGroupAvailable, setIsGroupAvailable] = useState(false);
	const [isCallActive, setIsCallActive] = useState(false);

	useEffect(() => {
		if (!groupId) return;

		setIsGroupAvailable(provider.isGroupAvailable(groupId));
		setIsCallActive(provider.isCallActive());

		provider.registerCallbacks({
			onReady: (isReady) => {
				setIsGroupAvailable(isReady ? provider.isGroupAvailable(groupId) : false);
			},
			onCallStarted: () => setIsCallActive(true),
			onCallEnded: () => {
				setIsCallActive(false);
				setIsGroupAvailable(provider.isGroupAvailable(groupId));
			},
			onUserListUpdated: () => {
				setIsGroupAvailable(provider.isGroupAvailable(groupId));
			},
		});

		return () => provider.unregisterCallbacks();
	}, [groupId, provider]);

	return {
		isGroupAvailable,
		isCallActive,
		callGroup: () => {
			if (!groupId) return;
			const success = provider.callGroup(groupId);
			if (!success) logger.logGeneral.error({}, `Failed to start call for group: ${groupId}`, 'useCallProvider.callGroup');
		},
	};
}
